<script setup lang="ts">
/**
 * 电子小票（顾客手机上打开的那一页）。
 *
 * ============================================================
 * 这一页就是「取餐凭据」
 * ============================================================
 * 业务规则（2026-10-13 定）：**不用纸质小票取餐，凭付完款生成的电子小票取餐。**
 * 所以这一页要在顾客手机上把三件事说清楚，缺一不可：
 *   1. **取餐号**（最大，店员核对的就是这个号）
 *   2. **付没付钱**（已付款 / 未付款 / 测试收款 —— 绝不能让店员拿一张没付钱的票出餐）
 *   3. **点了什么**（出餐时对单，也防「这单是不是我的」扯皮）
 *
 * 打开方式：触屏机付完款后屏幕上出二维码，顾客扫码存到手机（链接里带他自己的凭据）。
 * 凭据只对得上他自己那一单，所以链接被别人看到也翻不出别人的单。
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { ApiError, createApiClient } from '../api/client';
import { createEndpoints } from '../api/endpoints';
import type { CustomerOrderDetail } from '../api/types';
import { ensureDeviceId, resolveKioskConfig } from '../config';
import { parseReceiptQuery } from '../utils/receipt';
import { pickupStateFor } from '../utils/pickup';

const state = ref<{
  loading: boolean;
  error: string | null;
  order: CustomerOrderDetail | null;
}>({ loading: true, error: null, order: null });

const parsed = parseReceiptQuery(window.location.hash.split('?')[1] ?? '');

const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
const config = resolveKioskConfig(env, ensureDeviceId(null));
const endpoints = createEndpoints(createApiClient(config.apiBase));

let timer: ReturnType<typeof setInterval> | null = null;

/** 状态徽章的颜色：待取餐=绿，制作中=蓝，已完成=灰 */
const statusTone = computed(() => {
  switch (state.value.order?.status) {
    case 'READY': return 'ok';
    case 'COOKING':
    case 'ACCEPTED': return 'info';
    case 'COMPLETED': return 'muted';
    case 'CANCELLED': return 'danger';
    default: return 'warn';
  }
});

// 「能不能取」这条判断抽在 utils/pickup.ts（有单测），这里只负责显示
const pickupState = computed(() => pickupStateFor(state.value.order?.status));

const payTone = computed(() => {
  if (state.value.order?.payStatus === 'PAID') {
    return state.value.order?.isTestMode ? 'warn' : 'ok';
  }
  return 'danger';
});

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

async function load(): Promise<void> {
  if (!parsed) {
    state.value = { loading: false, error: '这个链接不完整，请让店员帮忙重新出示小票。', order: null };
    return;
  }
  try {
    const order = await endpoints.getOrderForCustomer(parsed.orderNo, { openId: parsed.credential });
    state.value = { loading: false, error: null, order };
  } catch (error) {
    const apiError = error as ApiError;
    state.value = {
      loading: false,
      error:
        apiError?.status === 404
          ? '查不到这张小票（可能链接不完整，或者这不是这台设备下的单）。'
          : apiError?.message ?? '小票加载失败，请让店员帮忙。',
      order: null,
    };
  }
}

onMounted(() => {
  void load();
  // 每 10 秒刷新一次：顾客能看着状态从「制作中」变成「待取餐」，不用一直问店员
  timer = setInterval(() => void load(), 10_000);
});

onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>

<template>
  <div class="receipt">
    <div v-if="state.loading && !state.order" class="loading">正在打开电子小票…</div>

    <div v-else-if="state.error" class="receipt__error">
      <div class="receipt__error-icon">⚠️</div>
      <p>{{ state.error }}</p>
      <button class="btn btn--primary" @click="load">重试</button>
    </div>

    <template v-else-if="state.order">
      <header class="receipt__head">
        <div class="receipt__brand">金骅 · 潮汕牛肉饭</div>
        <div class="receipt__title">电子小票</div>
      </header>

      <section class="receipt__pickup">
        <div class="receipt__pickup-label">取餐号</div>
        <div class="receipt__pickup-no">{{ state.order.pickupNo || state.order.orderNo }}</div>
      </section>

      <!-- 结论条：能不能取，一眼看到，不用顾客自己看状态猜 -->
      <section class="go" :class="`go--${pickupState.tone}`">
        <div class="go__title">{{ pickupState.title }}</div>
        <div class="go__note">{{ pickupState.note }}</div>
      </section>

      <section class="receipt__badges">
        <span class="badge" :class="`badge--${statusTone}`">{{ state.order.statusText }}</span>
        <span class="badge" :class="`badge--${payTone}`">
          {{ state.order.payStatus === 'PAID' ? (state.order.isTestMode ? '测试收款（非真实）' : '已付款') : '未付款' }}
        </span>
        <span v-if="state.order.table" class="badge badge--muted">
          {{ state.order.table.name || state.order.table.code }}
        </span>
      </section>

      <section class="receipt__card">
        <div v-for="item in state.order.items" :key="item.id" class="line">
          <div class="line__name">
            {{ item.productName }}
            <span v-if="item.variantText" class="muted">· {{ item.variantText }}</span>
            <div v-if="item.remark" class="line__remark">备注：{{ item.remark }}</div>
          </div>
          <div class="line__qty">×{{ item.quantity }}</div>
          <div class="line__amount">{{ item.subtotalText }}</div>
        </div>

        <div class="receipt__divider" />

        <div class="sumrow">
          <span class="muted">菜品小计</span><span>¥{{ state.order.amounts.itemsTotalText }}</span>
        </div>
        <div v-if="state.order.amounts.discountCents > 0" class="sumrow">
          <span class="muted">优惠</span><span>-¥{{ state.order.amounts.discountText }}</span>
        </div>
        <div v-if="state.order.amounts.packFeeCents > 0" class="sumrow">
          <span class="muted">餐盒费</span><span>¥{{ state.order.amounts.packFeeText }}</span>
        </div>
        <div class="sumrow sumrow--total">
          <span>应付</span><span class="amount">¥{{ state.order.amounts.payableText }}</span>
        </div>
        <div class="sumrow">
          <span class="muted">已付</span>
          <span :class="{ warn: state.order.isTestMode }">¥{{ state.order.amounts.paidText ?? '0.00' }}</span>
        </div>
      </section>

      <section class="receipt__meta">
        <div>订单号 {{ state.order.orderNo }}</div>
        <div>下单时间 {{ formatTime(state.order.placedAt) }}</div>
        <div v-if="state.order.paidAt">付款时间 {{ formatTime(state.order.paidAt) }}</div>
      </section>

      <div v-if="state.order.isTestMode" class="receipt__test">
        ⚠️ 这是<b>测试收款</b>（模拟支付），没有真的收到钱，也不能凭它出餐。
      </div>

      <div class="receipt__actions">
        <button class="btn btn--ghost" @click="load">刷新状态</button>
      </div>

      <p class="receipt__foot muted">
        这一页每 10 秒自动刷新；把屏幕给取餐口的店员看即可。
      </p>
    </template>
  </div>
</template>

<style scoped>
.receipt {
  max-width: 560px;
  margin: 0 auto;
  padding: 14px 14px 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.receipt__head {
  text-align: center;
}

.receipt__brand {
  color: var(--brand);
  font-size: 15px;
  letter-spacing: 3px;
  font-weight: 700;
}

.receipt__title {
  font-size: 22px;
  font-weight: 800;
  margin-top: 2px;
}

.receipt__pickup {
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 16px;
  text-align: center;
}

.receipt__pickup-label {
  color: var(--muted);
  font-size: 14px;
  letter-spacing: 4px;
}

/* 取餐号要「隔着柜台也看得清」，但不能溢出手机屏宽 */
.receipt__pickup-no {
  font-size: clamp(64px, 22vw, 120px);
  font-weight: 900;
  line-height: 1.05;
  color: #ffd08a;
  font-variant-numeric: tabular-nums;
  letter-spacing: 4px;
}

.receipt__pickup-hint {
  color: var(--muted);
  font-size: 14px;
}

/* 结论条：绿=可以取，橙=还得等 */
.go {
  border-radius: var(--radius);
  padding: 14px 16px;
  text-align: center;
  border: 1px solid var(--line);
  background: var(--panel);
}

.go__title {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: 2px;
}

.go__note {
  font-size: 14px;
  color: var(--muted);
  margin-top: 4px;
}

.go--ok {
  background: rgba(47, 169, 107, 0.16);
  border-color: rgba(47, 169, 107, 0.5);
}

.go--ok .go__title {
  color: #8ff0bd;
}

.go--wait {
  background: rgba(232, 163, 61, 0.16);
  border-color: rgba(232, 163, 61, 0.5);
}

.go--wait .go__title {
  color: #ffd08a;
}

.go--danger {
  background: rgba(224, 82, 82, 0.16);
  border-color: rgba(224, 82, 82, 0.5);
}

.go--danger .go__title {
  color: #ffb4b4;
}

.receipt__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.badge {
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  background: var(--panel-2);
}

.badge--ok { background: rgba(47, 169, 107, 0.2); color: #8ff0bd; }
.badge--info { background: rgba(59, 130, 246, 0.2); color: #bcd7ff; }
.badge--warn { background: rgba(232, 163, 61, 0.2); color: #ffd08a; }
.badge--danger { background: rgba(224, 82, 82, 0.22); color: #ffb4b4; }
.badge--muted { background: var(--panel-2); color: var(--muted); }

.receipt__card {
  background: var(--panel);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 12px 14px;
}

.line {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed var(--line);
}

.line:last-of-type {
  border-bottom: none;
}

.line__name {
  flex: 1;
  min-width: 0;
  font-size: 16px;
}

.line__remark {
  color: var(--muted);
  font-size: 13px;
  margin-top: 2px;
}

.line__qty {
  color: var(--muted);
  font-size: 15px;
  min-width: 34px;
  text-align: right;
}

.line__amount {
  min-width: 68px;
  text-align: right;
  font-size: 16px;
  font-variant-numeric: tabular-nums;
}

.receipt__divider {
  height: 1px;
  background: var(--line);
  margin: 10px 0;
}

.sumrow {
  display: flex;
  justify-content: space-between;
  font-size: 15px;
  padding: 3px 0;
}

.sumrow--total {
  font-size: 18px;
  font-weight: 800;
}

.warn {
  color: var(--warn);
}

.receipt__meta {
  color: var(--muted);
  font-size: 13px;
  line-height: 1.7;
  text-align: center;
}

.receipt__test {
  background: rgba(232, 163, 61, 0.16);
  border: 1px solid rgba(232, 163, 61, 0.4);
  color: #ffd08a;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 1.5;
}

.receipt__actions {
  display: flex;
  justify-content: center;
}

.receipt__foot {
  font-size: 12px;
  text-align: center;
}

.receipt__error {
  text-align: center;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: center;
  color: var(--muted);
}

.receipt__error-icon {
  font-size: 46px;
}
</style>
